import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { GitMerge, X } from 'lucide-react';
import api from '../utils/api';
import toast from 'react-hot-toast';

// Shared modal to merge multiple tickets into one primary.
// Used from the Tickets list (bulk select) and from a contact's recent-tickets list.
export default function MergeTicketsModal({ selectedTickets, onClose, onMerged }) {
  const { t } = useTranslation();
  const [primaryId, setPrimaryId] = useState(selectedTickets[0]?.id || '');
  const [merging, setMerging] = useState(false);

  const handleMerge = async (confirmDifferentContacts = false) => {
    const mergeIds = selectedTickets.map(tk => tk.id).filter(id => id !== primaryId);
    if (!mergeIds.length) { toast.error(t('tickets.selectDifferentPrimary')); return; }
    setMerging(true);
    try {
      await api.post('/tickets/merge', { primaryId, mergeIds, confirmDifferentContacts });
      toast.success(t('tickets.mergedSuccess', { count: mergeIds.length }));
      onMerged();
    } catch (err) {
      // The backend refuses a cross-contact merge until someone confirms it: the
      // absorbed contact silently stops receiving replies. Spell out who goes mute.
      if (err?.response?.status === 409 && err.response.data?.code === 'DIFFERENT_CONTACTS') {
        setMerging(false);
        const { primary, losing } = err.response.data;
        const names = losing.map(l => `#${l.ticket_number} (${l.contact_name || '—'})`).join(', ');
        if (window.confirm(t('tickets.mergeDifferentContactsWarning', {
          losing: names,
          primaryContact: primary.contact_name || '—',
          primaryTicket: primary.ticket_number,
        }))) {
          handleMerge(true);
        }
        return;
      }
      toast.error(t('tickets.mergeFailed'));
    } finally {
      setMerging(false);
    }
  };

  return createPortal(
    <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <h3>{t('tickets.mergeTitle', { count: selectedTickets.length })}</h3>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <p style={{ fontSize: 13, color: 'var(--c-text-muted)', marginBottom: 16 }}>
          {t('tickets.mergeDescription')}
        </p>

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: 'var(--c-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            {t('tickets.selectPrimary')}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {selectedTickets.map(tk => (
              <label key={tk.id} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                border: `2px solid ${primaryId === tk.id ? 'var(--c-brand)' : 'var(--c-border)'}`,
                background: primaryId === tk.id ? 'rgba(99,102,241,0.06)' : 'var(--c-surface-2)'
              }}>
                <input
                  type="radio"
                  name="primary"
                  value={tk.id}
                  checked={primaryId === tk.id}
                  onChange={() => setPrimaryId(tk.id)}
                  style={{ accentColor: 'var(--c-brand)' }}
                />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>#{tk.ticket_number} — {tk.subject}</div>
                  <div style={{ fontSize: 11, color: 'var(--c-text-muted)' }}>
                    {tk.company_name || tk.contact_name || tk.contact_email}
                    {primaryId === tk.id && <span style={{ marginLeft: 8, color: 'var(--c-brand)', fontWeight: 600 }}>← {t('tickets.primary')}</span>}
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary btn-sm" onClick={onClose}>{t('common.cancel')}</button>
          <button className="btn btn-primary btn-sm" onClick={() => handleMerge(false)} disabled={merging}>
            <GitMerge size={13} />
            {merging ? t('tickets.merging') : t('tickets.mergeInto') + ` #${selectedTickets.find(tk => tk.id === primaryId)?.ticket_number}`}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
